import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { buildAvatarUrl } from '@mysimpledb/shared/utils/mediaUrls';
import {
  acceptContactRequest,
  archiveConversation,
  blockMessagingUser,
  declineContactRequest,
  fetchConversations,
  fetchMessages,
  markConversationRead,
  sendConversationMessage,
  unarchiveConversation,
  unblockMessagingUser,
} from '../../api/messagingClient';
import './MessagesWeb.css';

const MAX_MESSAGE_LENGTH = 2000;
const NEAR_BOTTOM_THRESHOLD = 80;

function messagesAreEqual(current, next) {
  if (current === next) return true;
  if (!Array.isArray(current) || !Array.isArray(next)) return false;
  if (current.length !== next.length) return false;

  return current.every((message, index) => {
    const nextMessage = next[index];

    return (
      String(message?.id) === String(nextMessage?.id) &&
      String(message?.sender_user_id) === String(nextMessage?.sender_user_id) &&
      message?.message_text === nextMessage?.message_text &&
      message?.created_at === nextMessage?.created_at
    );
  });
}

const MESSAGE_FOLDERS = {
  inbox: 'inbox',
  archived: 'archived',
  blocked: 'blocked',
};

function isArchivedConversation(conversation) {
  return (
    conversation?.status === 'archived' ||
    Boolean(conversation?.archived_at) ||
    Boolean(conversation?.is_archived)
  );
}

function isBlockedConversation(conversation) {
  return (
    conversation?.status === 'blocked' ||
    Boolean(conversation?.blocked_at) ||
    Boolean(conversation?.is_blocked)
  );
}

const REQUEST_TYPE_LABELS = {
  entry_question: 'Question about a shared entry',
  collaboration: 'Collaboration request',
  service_inquiry: 'Service inquiry',
  other: 'Contact request',
};

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

function ConversationAvatar({ conversation }) {
  const imageUrl = conversation.other_user_profile_picture
    ? buildAvatarUrl(conversation.other_user_profile_picture)
    : null;

  if (imageUrl) {
    return (
      <img
        className="messages-avatar"
        src={imageUrl}
        alt=""
      />
    );
  }

  return (
    <div className="messages-avatar messages-avatar-placeholder">
      {(conversation.other_user_name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

export default function MessagesWeb({
  userId,
  initialConversationId = null,
}) {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pasteWasTruncated, setPasteWasTruncated] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [messageFolder, setMessageFolder] = useState(MESSAGE_FOLDERS.inbox);
  const messageListRef = useRef(null);
  const messageListEndRef = useRef(null);
  const messagesRef = useRef([]);
  const pendingScrollRef = useRef(null);
  const requestedConversationIdRef = useRef(
    initialConversationId ||
      sessionStorage.getItem('mysimpledb.openConversationId') ||
      new URLSearchParams(window.location.search).get('conversationId')
  );

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => String(conversation.id) === String(selectedId)
      ) || null,
    [conversations, selectedId]
  );

  const visibleConversations = useMemo(() => {
    return conversations.filter((conversation) => {
      const isArchived = isArchivedConversation(conversation);
      const isBlocked = isBlockedConversation(conversation);

      if (messageFolder === MESSAGE_FOLDERS.archived) {
        return isArchived && !isBlocked;
      }

      if (messageFolder === MESSAGE_FOLDERS.blocked) {
        return isBlocked;
      }

      return !isArchived && !isBlocked;
    });
  }, [conversations, messageFolder]);

  const folderCounts = useMemo(() => {
    return conversations.reduce(
      (counts, conversation) => {
        if (isBlockedConversation(conversation)) {
          counts.blocked += 1;
        } else if (isArchivedConversation(conversation)) {
          counts.archived += 1;
        } else {
          counts.inbox += 1;
        }

        return counts;
      },
      { inbox: 0, archived: 0, blocked: 0 }
    );
  }, [conversations]);

  const isMessageListNearBottom = useCallback(() => {
    const list = messageListRef.current;
    if (!list) return true;

    const distanceFromBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight;

    return distanceFromBottom <= NEAR_BOTTOM_THRESHOLD;
  }, []);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingConversations(true);

    try {
      const result = await fetchConversations();
      setConversations(result);
      setSelectedId((currentId) => {
        const requestedId = requestedConversationIdRef.current;

        if (
          requestedId &&
          result.some(
            (conversation) => String(conversation.id) === String(requestedId)
          )
        ) {
          requestedConversationIdRef.current = null;
          sessionStorage.removeItem('mysimpledb.openConversationId');
          return requestedId;
        }

        if (
          currentId &&
          result.some(
            (conversation) => String(conversation.id) === String(currentId)
          )
        ) {
          return currentId;
        }

        return result[0]?.id || null;
      });
      if (!silent) setError('');
    } catch (err) {
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  }, []);

  const loadMessages = useCallback(
    async (conversationId, { silent = false } = {}) => {
      if (!conversationId) {
        messagesRef.current = [];
        setMessages([]);
        return;
      }

      const wasNearBottom = isMessageListNearBottom();

      if (!silent) setLoadingMessages(true);

      try {
        const result = await fetchMessages(conversationId);
        const currentMessages = messagesRef.current;
        const messagesChanged = !messagesAreEqual(currentMessages, result);

        if (messagesChanged) {
          if (!silent) {
            // Opening/switching conversations should start at the newest message.
            pendingScrollRef.current = 'auto';
          } else if (wasNearBottom) {
            // New incoming messages follow the bottom only if the user was already there.
            pendingScrollRef.current = 'smooth';
          }

          messagesRef.current = result;
          setMessages(result);
        } else if (!silent) {
          // Even if the data is unchanged, opening a conversation should land at the end.
          pendingScrollRef.current = 'auto';
          requestAnimationFrame(() => {
            const list = messageListRef.current;
            if (!list) return;

            list.scrollTop = list.scrollHeight;
            pendingScrollRef.current = null;
          });
        }

        await markConversationRead(conversationId);
        setConversations((current) =>
          current.map((conversation) =>
            String(conversation.id) === String(conversationId)
              ? { ...conversation, unread_count: 0 }
              : conversation
          )
        );
        if (!silent) setError('');
      } catch (err) {
        if (!silent) setError(err.message);
      } finally {
        if (!silent) setLoadingMessages(false);
      }
    },
    [isMessageListNearBottom]
  );

  useEffect(() => {
    requestedConversationIdRef.current =
      initialConversationId ||
      sessionStorage.getItem('mysimpledb.openConversationId') ||
      new URLSearchParams(window.location.search).get('conversationId');

    loadConversations();
  }, [initialConversationId, loadConversations]);

  useEffect(() => {
    if (selectedId) loadMessages(selectedId);
  }, [loadMessages, selectedId]);

  useEffect(() => {
    const selectedIsVisible = visibleConversations.some(
      (conversation) => String(conversation.id) === String(selectedId)
    );

    if (selectedIsVisible) return;

    const nextSelectedId = visibleConversations[0]?.id || null;
    setSelectedId(nextSelectedId);

    if (!nextSelectedId) {
      messagesRef.current = [];
      setMessages([]);
    }
  }, [messageFolder, selectedId, visibleConversations]);

  useLayoutEffect(() => {
    messagesRef.current = messages;

    const scrollMode = pendingScrollRef.current;
    if (!scrollMode || loadingMessages) return;

    const list = messageListRef.current;
    if (!list) return;

    pendingScrollRef.current = null;

    if (scrollMode === 'smooth') {
      list.scrollTo({
        top: list.scrollHeight,
        behavior: 'smooth',
      });
    } else {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, loadingMessages]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadConversations({ silent: true });
      if (selectedId) loadMessages(selectedId, { silent: true });
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [loadConversations, loadMessages, selectedId]);

  const updateConversationStatus = (conversationId, status) => {
    setConversations((current) =>
      current.map((conversation) =>
        String(conversation.id) === String(conversationId)
          ? { ...conversation, status }
          : conversation
      )
    );
  };

  const handleResponse = async (response) => {
    if (!selectedConversation || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      if (response === 'accept') {
        await acceptContactRequest(selectedConversation.id);
        updateConversationStatus(selectedConversation.id, 'active');
      } else {
        await declineContactRequest(selectedConversation.id);
        updateConversationStatus(selectedConversation.id, 'declined');
      }
      await loadConversations({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDraftChange = (event) => {
    const nextDraft = event.target.value;
    setDraft(nextDraft);

    if (nextDraft.length < MAX_MESSAGE_LENGTH) {
      setPasteWasTruncated(false);
    }
  };

  const handleDraftPaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const textarea = event.currentTarget;
    const selectionStart = textarea.selectionStart ?? draft.length;
    const selectionEnd = textarea.selectionEnd ?? draft.length;
    const selectedLength = Math.max(0, selectionEnd - selectionStart);
    const resultingLength = draft.length - selectedLength + pastedText.length;

    setPasteWasTruncated(resultingLength > MAX_MESSAGE_LENGTH);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const messageText = draft.trim();
    if (!selectedConversation || !messageText || submitting) return;

    setSubmitting(true);
    setError('');
    try {
      const message = await sendConversationMessage(
        selectedConversation.id,
        messageText
      );
      pendingScrollRef.current = 'smooth';
      setMessages((current) => {
        const nextMessages = [...current, message];
        messagesRef.current = nextMessages;
        return nextMessages;
      });
      setDraft('');
      setPasteWasTruncated(false);
      await loadConversations({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedConversation || submitting) return;
    setSubmitting(true);
    try {
      await archiveConversation(selectedConversation.id);
      setConversations((current) =>
        current.map((conversation) =>
          String(conversation.id) === String(selectedConversation.id)
            ? {
                ...conversation,
                is_archived: true,
                archived_at: conversation.archived_at || new Date().toISOString(),
              }
            : conversation
        )
      );
      setMessageFolder(MESSAGE_FOLDERS.archived);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnarchive = async () => {
    if (!selectedConversation || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      await unarchiveConversation(selectedConversation.id);
      setConversations((current) =>
        current.map((conversation) =>
          String(conversation.id) === String(selectedConversation.id)
            ? {
                ...conversation,
                is_archived: false,
                archived_at: null,
              }
            : conversation
        )
      );
      setMessageFolder(MESSAGE_FOLDERS.inbox);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlock = async () => {
    if (!selectedConversation || submitting) return;
    const confirmed = window.confirm(
      `Block ${selectedConversation.other_user_name}? This will close the conversation.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      await blockMessagingUser(selectedConversation.other_user_id);
      setConversations((current) =>
        current.map((conversation) =>
          String(conversation.id) === String(selectedConversation.id)
            ? {
                ...conversation,
                status: 'closed',
                is_blocked: true,
                blocked_at: conversation.blocked_at || new Date().toISOString(),
              }
            : conversation
        )
      );
      setMessageFolder(MESSAGE_FOLDERS.blocked);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async () => {
    if (!selectedConversation || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      await unblockMessagingUser(selectedConversation.other_user_id);
      setConversations((current) =>
        current.map((conversation) =>
          Number(conversation.other_user_id) ===
          Number(selectedConversation.other_user_id)
            ? {
                ...conversation,
                status: conversation.status === 'closed' ? 'active' : conversation.status,
                is_blocked: false,
                blocked_at: null,
              }
            : conversation
        )
      );
      setMessageFolder(MESSAGE_FOLDERS.inbox);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isRequestRecipient =
    selectedConversation?.status === 'pending' &&
    String(selectedConversation.recipient_user_id) === String(userId);

  return (
    <main className="messages-page">
      <div className="messages-shell">
        <aside className="messages-sidebar">
          <div className="messages-sidebar-header">
            <div>
              <span>MySimpleDB</span>
              <h1>Messages</h1>
            </div>
            <button type="button" onClick={() => loadConversations()}>
              Refresh
            </button>
          </div>

          <nav className="messages-folder-tabs" aria-label="Message folders">
            {[
              [MESSAGE_FOLDERS.inbox, 'Inbox', folderCounts.inbox],
              [MESSAGE_FOLDERS.archived, 'Archived', folderCounts.archived],
              [MESSAGE_FOLDERS.blocked, 'Blocked', folderCounts.blocked],
            ].map(([folder, label, count]) => (
              <button
                key={folder}
                type="button"
                className={`messages-folder-tab${
                  messageFolder === folder ? ' is-active' : ''
                }`}
                aria-current={messageFolder === folder ? 'page' : undefined}
                onClick={() => setMessageFolder(folder)}
              >
                <span>{label}</span>
                <span className="messages-folder-count">{count}</span>
              </button>
            ))}
          </nav>

          {loadingConversations && (
            <div className="messages-empty-state">Loading conversations…</div>
          )}

          {!loadingConversations && visibleConversations.length === 0 && (
            <div className="messages-empty-state">
              <strong>
                {messageFolder === MESSAGE_FOLDERS.inbox
                  ? 'No messages yet'
                  : messageFolder === MESSAGE_FOLDERS.archived
                    ? 'No archived conversations'
                    : 'No blocked conversations'}
              </strong>
              <p>
                {messageFolder === MESSAGE_FOLDERS.inbox
                  ? 'Contact requests and conversations will appear here.'
                  : messageFolder === MESSAGE_FOLDERS.archived
                    ? 'Conversations you archive will appear here.'
                    : 'Users you block will appear here.'}
              </p>
            </div>
          )}

          <div className="messages-conversation-list">
            {visibleConversations.map((conversation) => (
              <button
                key={conversation.id}
                type="button"
                className={`messages-conversation-row${
                  String(conversation.id) === String(selectedId)
                    ? ' is-selected'
                    : ''
                }`}
                onClick={() => setSelectedId(conversation.id)}
              >
                <ConversationAvatar conversation={conversation} />
                <span className="messages-conversation-summary">
                  <span className="messages-conversation-name-line">
                    <strong>{conversation.other_user_name}</strong>
                    <time>{formatDate(conversation.last_message_at)}</time>
                  </span>
                  <span className="messages-conversation-preview">
                    {conversation.latest_message_text ||
                      REQUEST_TYPE_LABELS[conversation.request_type]}
                  </span>
                  {conversation.status === 'pending' && (
                    <span className="messages-status-pill">Pending</span>
                  )}
                  {isArchivedConversation(conversation) &&
                    !isBlockedConversation(conversation) && (
                      <span className="messages-status-pill is-archived">
                        Archived
                      </span>
                    )}
                  {isBlockedConversation(conversation) && (
                    <span className="messages-status-pill is-blocked">
                      Blocked
                    </span>
                  )}
                </span>
                {Number(conversation.unread_count) > 0 && (
                  <span className="messages-unread-count">
                    {conversation.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </aside>

        <section className="messages-thread">
          {!selectedConversation && (
            <div className="messages-thread-empty">
              <strong>Select a conversation</strong>
              <p>Choose a message from the list to view it.</p>
            </div>
          )}

          {selectedConversation && (
            <>
              <header className="messages-thread-header">
                <div className="messages-thread-person">
                  <ConversationAvatar conversation={selectedConversation} />
                  <div>
                    <h2>{selectedConversation.other_user_name}</h2>
                    <span>
                      {REQUEST_TYPE_LABELS[selectedConversation.request_type]}
                    </span>
                  </div>
                </div>
                <div className="messages-thread-actions">
                  {messageFolder === MESSAGE_FOLDERS.inbox && (
                    <>
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={handleArchive}
                      >
                        Archive
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        disabled={submitting}
                        onClick={handleBlock}
                      >
                        Block
                      </button>
                    </>
                  )}
                  {messageFolder === MESSAGE_FOLDERS.archived && (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={handleUnarchive}
                    >
                      Unarchive
                    </button>
                  )}
                  {messageFolder === MESSAGE_FOLDERS.blocked && (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={handleUnblock}
                    >
                      Unblock
                    </button>
                  )}
                </div>
              </header>

              {selectedConversation.status === 'pending' && (
                <div className="messages-request-banner">
                  {isRequestRecipient ? (
                    <>
                      <div>
                        <strong>Contact request</strong>
                        <p>
                          Accept this request to begin exchanging messages.
                        </p>
                      </div>
                      <div className="messages-request-actions">
                        <button
                          type="button"
                          className="is-primary"
                          disabled={submitting}
                          onClick={() => handleResponse('accept')}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => handleResponse('decline')}
                        >
                          Decline
                        </button>
                      </div>
                    </>
                  ) : (
                    <p>Waiting for this user to accept your contact request.</p>
                  )}
                </div>
              )}

              {selectedConversation.related_entry_id && (
                <div className="messages-related-entry">
                  Related shared entry #{selectedConversation.related_entry_id}
                </div>
              )}

              {error && <div className="messages-error">{error}</div>}

              <div
                ref={messageListRef}
                className="messages-message-list"
                aria-live="polite"
              >
                {loadingMessages ? (
                  <div className="messages-thread-empty">Loading messages…</div>
                ) : (
                  messages.map((message) => {
                    const isOwn =
                      String(message.sender_user_id) === String(userId);
                    return (
                      <div
                        key={message.id}
                        className={`messages-message${
                          isOwn ? ' is-own' : ''
                        }`}
                      >
                        <div>{message.message_text}</div>
                        <time>{formatDate(message.created_at)}</time>
                      </div>
                    );
                  })
                )}
                <div ref={messageListEndRef} />
              </div>

              {messageFolder === MESSAGE_FOLDERS.inbox &&
                selectedConversation.status === 'active' && (
                <form className="messages-composer" onSubmit={handleSend}>
                  <label htmlFor="conversation-message">Message</label>
                  <textarea
                    id="conversation-message"
                    rows={3}
                    maxLength={MAX_MESSAGE_LENGTH}
                    value={draft}
                    placeholder="Write a message…"
                    onChange={handleDraftChange}
                    onPaste={handleDraftPaste}
                  />
                  {draft.length === MAX_MESSAGE_LENGTH && (
                    <div
                      className="messages-composer-limit-message"
                      role="status"
                    >
                      {pasteWasTruncated
                        ? `Only the first ${MAX_MESSAGE_LENGTH.toLocaleString()} characters were included.`
                        : `Maximum message length reached (${MAX_MESSAGE_LENGTH.toLocaleString()} characters).`}
                    </div>
                  )}
                  <div className="messages-composer-footer">
                    <span
                      className={
                        draft.length === MAX_MESSAGE_LENGTH ? 'is-at-limit' : ''
                      }
                    >
                      {draft.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                    <button
                      type="submit"
                      disabled={!draft.trim() || submitting}
                    >
                      {submitting ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </form>
              )}

              {messageFolder !== MESSAGE_FOLDERS.blocked &&
                ['declined', 'closed'].includes(selectedConversation.status) && (
                <div className="messages-conversation-closed">
                  This conversation is closed.
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
